import {
  Box,
  Button,
  CircularProgress,
  Palette,
  Stack,
  Typography,
} from "@mui/material";
import { Download, FileText } from "lucide-react";
import { useState } from "react";
import { IChatBlock } from "@pages/askDost/types";
import { borderGeneralStyle } from "@utils/cssStyles";
import { downloadFile } from "@pages/askDost/services/askDostService";
import { getIconBadgeStyle } from "@pages/askDost/styles/askDostStyles";
import {
  fileBlockStackSx,
  fileNameSx,
} from "@pages/askDost/styles/FileBlockStyle";

interface IFileBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

export const FileBlock: React.FC<IFileBlockProps> = ({
  block,
  palette,
  translate,
}) => {
  const fileId = block?.fileId;
  const fileName = block?.fileName;
  const [isDownloading, setIsDownloading] = useState(false);

  if (!fileId || !fileName) return null;

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await downloadFile(fileId);

      if (!response?.ok) {
        console.error("Failed_to_download:", response?.status);
        return;
      }

      const blob = await response?.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      if (link?.parentNode) link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error_downloading_file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={fileBlockStackSx(palette)}
    >
      <Box sx={getIconBadgeStyle(palette, { variant: "filled" })}>
        <FileText size={16} color={palette.primary.dark} />
      </Box>
      <Typography variant="body2" noWrap sx={fileNameSx(palette)}>
        {fileName}
      </Typography>

      <Button
        size="small"
        variant="outlined"
        disabled={isDownloading}
        startIcon={
          isDownloading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Download size={16} />
          )
        }
        onClick={handleDownload}
        sx={{ flexShrink: 0, textTransform: "none" }}
      >
        {isDownloading ? translate("downloading") : translate("download")}
      </Button>
    </Stack>
  );
};
